import {
    Box,
    Button,
    Card,
    CardMedia,
    Grid,
    Typography,
    Skeleton,
    styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { backendURL } from "redux/reducers/settings";
import { useFetchGalleryByRoutineId } from "service/user.query";
import { handleCopy } from "utils/copy";

// Styled component for a polished hover effect
const StyledCard = styled(Card)(({ theme }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[8],
        cursor: "pointer",
    },
}));

const PAGE_SIZE = 12;

interface GalleryImage {
    id: string | number;
    image_path: string;
    title?: string;
    row_total?: number;
}

const UserGalleryList = () => {
    const { training_id: routineId } = useParams<{ training_id: string }>();

    const [page, setPage] = useState(1);

    const { data: galleryData, isLoading, isFetching } = useFetchGalleryByRoutineId(
        routineId ?? "0",
        !!routineId,
        { pageNo: page, pageSize: PAGE_SIZE }
    );

    const images: GalleryImage[] = galleryData ?? [];
    const totalRows: number = images.length > 0 ? images[0].row_total ?? 0 : 0;
    const totalPages = Math.ceil(totalRows / PAGE_SIZE);
    const canNext = page < totalPages;
    const canPrevious = page > 1;

    // back to the first page whenever the routine changes
    useEffect(() => {
        setPage(1);
    }, [routineId]);

    // --- Loading State ---
    if (isLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width={150} height={40} sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (!images.length) {
        return (
            <Box sx={{ p: 4, textAlign: "center", border: "1px dashed grey", borderRadius: 2 }}>
                <Typography color="text.secondary">No images found in this gallery.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
                Gallery ({totalRows})
            </Typography>

            <Grid container spacing={2}>
                {images.map((image: GalleryImage) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}
                        onClick={() => {
                            handleCopy(backendURL + image.image_path)
                        }}
                    >
                        <StyledCard elevation={2}>
                            <CardMedia
                                component="img"
                                sx={{
                                    aspectRatio: "1/1", // Keeps images square and uniform
                                    objectFit: "cover",
                                }}
                                image={backendURL + image.image_path}
                                alt={image.title || "Gallery image"}
                                loading="lazy" // Performance boost
                            />
                            {image.title && (
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="body2" noWrap fontWeight={500}>
                                        {image.title}
                                    </Typography>
                                </Box>
                            )}
                        </StyledCard>
                    </Grid>
                ))}
            </Grid>

            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 2,
                        mt: 3,
                    }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!canPrevious || isFetching}
                        onClick={() => {
                            if (canPrevious) setPage((prev) => prev - 1);
                        }}
                    >
                        Previous
                    </Button>
                    <Typography variant="body2">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!canNext || isFetching}
                        onClick={() => {
                            if (canNext) setPage((prev) => prev + 1);
                        }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default UserGalleryList;
